"use client";

import { Button } from "@/components/ui/Button";
import { ArrowLeft, ArrowRight } from "@/components/ui/icons/Arrow";

type Props = {
  onNext: () => void;
  onBack?: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
  secondary?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    /** Same soft-block contract as onNextBlocked below, applied to the
     *  secondary button — see that comment. */
    onBlocked?: () => void;
  };
  /** When supplied alongside nextDisabled, the CTA renders visually disabled
   *  (aria-disabled) but stays clickable/focusable and calls this instead of
   *  onNext — lets a blocked tap reveal what's missing instead of doing
   *  nothing. Without it, nextDisabled falls back to the native `disabled`
   *  attribute as before. */
  onNextBlocked?: () => void;
};

export function NavFooter({
  onNext,
  onBack,
  nextDisabled,
  nextLabel = "Next",
  secondary,
  onNextBlocked,
}: Props) {
  const softBlocked = Boolean(nextDisabled && onNextBlocked);
  const secondarySoftBlocked = Boolean(secondary?.disabled && secondary?.onBlocked);
  return (
    <div
      // md:mt-auto pushes this to the bottom of QuizStepShell's now
      // viewport-height column (see PhotoSplit.tsx/QuizStepShell.tsx), so
      // it lands at the same y on both quiz steps regardless of how much
      // form content precedes it. pt-4 stays alongside it, not instead of
      // it: on mobile the column has no min-height, mt-auto collapses to
      // 0, and pt-4 is what keeps a 16px gap above the last question.
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-4 md:mt-auto"
    >
      {onBack ? (
        <Button
          variant="link"
          size="sm-base"
          type="button"
          onClick={onBack}
          // pl-0 flushes the arrow to the left edge, aligning it with the
          // Yes pill above it (design handoff: header-over-photo README §5).
          className="gap-1.5 pl-0"
        >
          <ArrowLeft />
          Back
        </Button>
      ) : (
        <span />
      )}
      <div className="ml-auto flex items-center gap-3">
        {secondary && (
          <Button
            type="button"
            variant="link"
            size="sm-base"
            onClick={secondarySoftBlocked ? secondary.onBlocked : secondary.onClick}
            disabled={secondary.disabled && !secondarySoftBlocked}
            aria-disabled={secondarySoftBlocked || undefined}
            className="aria-disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:hover:text-primary"
          >
            {secondary.label}
          </Button>
        )}
        <Button
          type="button"
          variant="cta"
          size="md-base"
          onClick={softBlocked ? onNextBlocked : onNext}
          disabled={nextDisabled && !softBlocked}
          aria-disabled={softBlocked || undefined}
          // Soft-blocked state must look identical to the native disabled
          // look (same opacity/cursor) but stay clickable — see softBlocked
          // above. Tailwind's built-in aria-disabled: variant compiles to
          // &[aria-disabled="true"], which beats variantClass.cta's own
          // hover:bg-cta-hover on specificity (not just class order), so
          // hovering a blocked button doesn't light up.
          className="gap-2 aria-disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:hover:bg-cta"
        >
          {nextLabel}
          <ArrowRight />
        </Button>
      </div>
    </div>
  );
}
